import React, { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Button,
  List,
  ListItem,
  Chip,
  Box,
  CircularProgress,
  Avatar,
  Grid,
  Paper,
  alpha,
  useTheme,
} from '@mui/material';
import {
  ShowChart as ChartIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import PageAlert from './PageAlert';
import PageLoader from './PageLoader';
import ConnectPlatformsCTA from './ConnectPlatformsCTA';

const RankContent = ({ onRank, loading, ranked, error }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const hasAutoFetched = useRef(false);

  // Unified error extraction
  const errorMsg = typeof error === 'string' ? error : (error?.message || '');

  // Detect setup/connectivity state
  const isSetupRequired = !!errorMsg.toLowerCase().match(/no .*accounts|link.*platforms|social.*media.*setup/i);

  // One-shot auto-fetch on mount
  useEffect(() => {
    if (!ranked?.length && !loading && !errorMsg && !hasAutoFetched.current && onRank) {
      hasAutoFetched.current = true;
      onRank();
    }
  }, [onRank, ranked, loading, errorMsg]);

  const handleRankClick = useCallback(() => {
    onRank();
  }, [onRank]);

  // Priority Early Return: If accounts aren't connected, show the "Locked" onboarding state
  if (isSetupRequired) {
    return (
      <Box sx={{ py: 6 }}>
        <ConnectPlatformsCTA
          title="Rankings Locked"
          description="Connect your social media platforms in Settings to unlock AI-powered content rankings and performance insights."
          buttonText="Link Your Platforms"
        />
      </Box>
    );
  }

  const getRankColor = (index) => {
    switch (index) {
      case 0: return '#6366f1'; // Indigo
      case 1: return '#8b5cf6'; // Violet
      case 2: return '#f59e0b'; // Amber
      default: return '#64748b'; // Slate
    }
  };

  const getRankIcon = (index) => {
    switch (index) {
      case 0: return '🥇';
      case 1: return '🥈';
      case 2: return '🥉';
      default: return `#${index + 1}`;
    }
  };

  const getRankBgColor = (index) => {
    switch (index) {
      case 0: return alpha('#6366f1', 0.08);
      case 1: return alpha('#8b5cf6', 0.08);
      case 2: return alpha('#f59e0b', 0.08);
      default: return alpha('#64748b', 0.08);
    }
  };

  return (
    <Box>
      {/* Dynamic Header */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h3" sx={{ fontWeight: 900, letterSpacing: '-0.03em', mb: 1.5 }}>
          Content Rankings
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
          AI-powered performance analysis across your active channels
        </Typography>
      </Box>

      <PageAlert error={error} />

      <Grid container spacing={5} alignItems="center">
        <Grid xs={12} lg={4}>
          <Box sx={{ position: 'sticky', top: 24 }}>
            <Paper
              elevation={0}
              sx={{
                p: 4,
                borderRadius: 5,
                background: alpha(theme.palette.primary.main, 0.03),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                display: 'flex',
                flexDirection: 'column',
                gap: 3
              }}
            >
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, mb: 1.5 }}>
                  Strategy Sync
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                  Our AI engine is ready to analyze your latest posts. We'll crunch likes, views, and comments to reveal your winning formula.
                </Typography>
              </Box>

              <Button
                variant="contained"
                size="large"
                fullWidth
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <ChartIcon />}
                onClick={handleRankClick}
                disabled={loading}
                sx={{
                  borderRadius: 4,
                  py: 2,
                  fontWeight: 800,
                  fontSize: '1rem',
                  background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                  boxShadow: '0 8px 20px -4px rgba(99, 102, 241, 0.4)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 12px 28px -4px rgba(99, 102, 241, 0.5)',
                  },
                }}
              >
                {loading ? 'Crunching...' : 'Refresh Rankings'}
              </Button>
            </Paper>
          </Box>
        </Grid>

        <Grid xs={12} lg={8}>
          {ranked && ranked.length > 0 ? (
            <Box>
              <Typography variant="overline" sx={{ fontWeight: 900, color: 'text.disabled', letterSpacing: 2, mb: 3, display: 'block' }}>
                TOP PERFORMING PLATFORMS
              </Typography>
              <List sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {ranked.map((item, index) => (
                  <ListItem
                    key={item.platform}
                    sx={{
                      p: 3,
                      borderRadius: 5,
                      background: getRankBgColor(index),
                      border: '1px solid',
                      borderColor: alpha(getRankColor(index), 0.15),
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'scale(1.02) translateX(8px)',
                        background: alpha(getRankColor(index), 0.12),
                        borderColor: alpha(getRankColor(index), 0.3),
                        boxShadow: `0 12px 30px -10px ${alpha(getRankColor(index), 0.25)}`
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 3 }}>
                      <Avatar
                        sx={{
                          bgcolor: getRankColor(index),
                          color: 'white',
                          width: 60,
                          height: 60,
                          fontSize: '1.6rem',
                          fontWeight: 900,
                          boxShadow: `0 10px 20px -5px ${alpha(getRankColor(index), 0.5)}`
                        }}
                      >
                        {getRankIcon(index)}
                      </Avatar>

                      <Box sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                          <Typography variant="h5" sx={{ fontWeight: 900, textTransform: 'capitalize' }}>
                            {item.platform}
                          </Typography>
                          <Chip
                            label={`Engagement: ${item.score?.toFixed(1) || '0'}`}
                            size="small"
                            sx={{
                              bgcolor: 'background.paper',
                              color: getRankColor(index),
                              fontWeight: 900,
                              fontSize: '0.75rem',
                              border: '1px solid',
                              borderColor: alpha(getRankColor(index), 0.2)
                            }}
                          />
                        </Box>
                        <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500, lineHeight: 1.6 }}>
                          {item.description || 'Analyzing growth patterns...'}
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 2.5, mt: 2 }}>
                          <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: getRankColor(index) }} />
                            {item.views?.toLocaleString()} Views
                          </Typography>
                          <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: getRankColor(index) }} />
                            {item.post_count} Posts
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ textAlign: 'right', display: { xs: 'none', md: 'block' } }}>
                        <Typography variant="h1" sx={{ fontWeight: 900, color: alpha(getRankColor(index), 0.15), lineHeight: 1 }}>
                          #{index + 1}
                        </Typography>
                      </Box>
                    </Box>
                  </ListItem>
                ))}
              </List>
            </Box>
          ) : !loading ? (
            <Box sx={{ textAlign: 'center', py: 12 }}>
              <Box
                sx={{
                  width: 140,
                  height: 140,
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 4,
                  border: '2px dashed',
                  borderColor: alpha(theme.palette.primary.main, 0.2)
                }}
              >
                <ChartIcon sx={{ fontSize: 56, color: alpha(theme.palette.primary.main, 0.3) }} />
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 900, mb: 1.5 }}>
                Ready to Analyze?
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', mb: 4, maxWidth: 500, mx: 'auto', fontWeight: 500, lineHeight: 1.7 }}>
                We haven't found any active posts or connected accounts yet. Link your platforms to see where you rank and get strategic growth insights.
              </Typography>
              <Button
                variant="outlined"
                startIcon={<SettingsIcon />}
                onClick={() => navigate('/app/settings')}
                sx={{
                  borderRadius: 4,
                  px: 4,
                  py: 1.5,
                  fontWeight: 800,
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    bgcolor: alpha(theme.palette.primary.main, 0.05)
                  }
                }}
              >
                Connect Platforms
              </Button>
            </Box>
          ) : (
            <Box sx={{ py: 4 }}>
              <PageLoader type="list" count={5} />
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default RankContent;